import React from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
} from "@mui/material";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { faGithub, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  CONTACT,
  PROFILE,
  HIGHLIGHTS,
  SKILLS,
  openWhatsApp,
} from "../../utils/constants";

const Hero = () => {
  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
        background:
          "radial-gradient(circle at top right, rgba(59,130,246,0.14), transparent 35%), radial-gradient(circle at bottom left, rgba(124,58,237,0.12), transparent 30%)",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Chip
                label="Available for selected opportunities"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  backgroundColor: "primary.main",
                  color: "white",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  lineHeight: 1.05,
                  mb: 2,
                }}
              >
                {CONTACT.fullName}
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  color: "primary.main",
                  mb: 2,
                  fontSize: { xs: "1.35rem", md: "1.9rem" },
                }}
              >
                {CONTACT.role}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1.08rem",
                  lineHeight: 1.8,
                  maxWidth: 760,
                  mb: 3,
                }}
              >
                {PROFILE.headline}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1rem",
                  lineHeight: 1.8,
                  maxWidth: 760,
                  mb: 4,
                }}
              >
                {PROFILE.summary}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 4 }}
              >
                {HIGHLIGHTS.map((item) => (
                  <Chip key={item} label={item} variant="outlined" />
                ))}
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="/projects"
                  startIcon={<FontAwesomeIcon icon={faEye} />}
                >
                  View Projects
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={openWhatsApp}
                  startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
                >
                  WhatsApp Me
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href={CONTACT.cvView}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                >
                  View CV
                </Button>

                <Button
                  variant="text"
                  size="large"
                  component="a"
                  href="/contact"
                  startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                >
                  Contact
                </Button>
              </Stack>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <Card
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    color: "white",
                    background: "linear-gradient(135deg, #0f172a, #2563eb)",
                    textAlign: "center",
                  }}
                >
                  <Avatar
                    src={CONTACT.profileImage}
                    alt={CONTACT.fullName}
                    imgProps={{
                      loading: "eager",
                      referrerPolicy: "no-referrer",
                    }}
                    sx={{
                      width: 150,
                      height: 150,
                      mx: "auto",
                      mb: 2,
                      border: "4px solid rgba(255,255,255,0.85)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                    }}
                  />
                  <Typography variant="overline" sx={{ opacity: 0.9 }}>
                    Featured Product
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    TapDosh
                  </Typography>
                  <Typography sx={{ opacity: 0.9, mt: 1 }}>
                    Commerce, delivery, connection and safer communities
                  </Typography>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                    A production-focused web and mobile ecosystem covering food
                    ordering, package delivery, payments, TapDate, TapChat,
                    administration, and SafeDosh community services.
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 3 }}
                  >
                    {[
                      "React",
                      "React Native",
                      "Node.js",
                      "Supabase",
                      "Product Engineering",
                    ].map((item) => (
                      <Chip key={item} label={item} size="small" />
                    ))}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Product areas built within TapDosh
                  </Typography>

                  <Box
                    component="ul"
                    sx={{ pl: 2.5, color: "text.secondary", mb: 3 }}
                  >
                    <li>Food, shops, gifts, and marketplace ordering</li>
                    <li>Package delivery and rider operations</li>
                    <li>TapDate profiles, matching, and TapChat</li>
                    <li>Payments, TapCredits, notifications, and SafeDosh</li>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      variant="contained"
                      component="a"
                      href="https://tapdosh.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View TapDosh
                    </Button>
                    <Button
                      variant="outlined"
                      component="a"
                      href={CONTACT.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<FontAwesomeIcon icon={faGithub} />}
                    >
                      GitHub
                    </Button>
                  </Stack>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Current focus
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {SKILLS.exploring.map((skill) => (
                        <Chip key={skill} label={skill} variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
